/**
 * Custom Label Component
 *
 * Displays the current value and percentage change at the end of each bar.
 * Shows the value in black and the percentage change in green (positive) or red (negative).
 */

import React from 'react';
import clsx from 'clsx';
import { HorizontalBarChartData } from './HorizontalBarChart';
import styles from './CustomLabel.module.scss';

interface CustomLabelProps {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    index?: number;
    data: HorizontalBarChartData[];
    isCurrency?: boolean;
}

// Calculate percentage change
const calculatePercentageChange = (current: number, previous: number): { text: string; isPositive: boolean } => {
    const change = previous > 0 ? ((current - previous) / previous) * 100 : 0;
    const isPositive = change >= 0;
    return {
        text: `${isPositive ? '+' : ''}${Math.round(change)}%`,
        isPositive
    };
};

export const CustomLabel: React.FC<CustomLabelProps> = ({ x, y, width, height, index, data, isCurrency = false }) => {
    if (x === undefined || y === undefined || width === undefined || height === undefined || index === undefined) {
        return null;
    }

    const item = data[index];
    const { text: changeText, isPositive } = calculatePercentageChange(item.currentValue, item.previousValue);

    const formatValue = (value: number): string => {
        return isCurrency ? `$${value.toLocaleString()}` : value.toString();
    };

    return (
        <foreignObject
            x={x + width + 10}
            y={y}
            width="200"
            height={height}
        >
            <div className={styles.labelContainer}>
                <span className={styles.value}>{formatValue(item.currentValue)}</span>
                <span className={clsx(styles.percentage, isPositive ? styles.positive : styles.negative)}>
                    {changeText}
                </span>
            </div>
        </foreignObject>
    );
};
